import { TimeInput } from '@mantine/dates';
import { formatDate } from '@/services/date';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import { getOnCallEndDateLabel } from '@bariendo/lib/on-call';
import { PractitionerOption } from '@/hooks/usePractitionersByRoles';
import { OnCallRowState, RowSaveState } from '../hooks/useOnCallSchedule';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Checkbox, Group, Loader, Select, Table, Tooltip } from '@mantine/core';

interface OnCallScheduleRowProps {
  row: OnCallRowState;
  selected: boolean;
  practitionerOptions: PractitionerOption[];
  practitionersLoading: boolean;
  saveState?: RowSaveState;
  disabled?: boolean;
  /** Hides the select checkbox for viewers, whose only bulk action would be a no-op. */
  selectable?: boolean;
  onSelectChange: (selected: boolean) => void;
  onSave: (row: OnCallRowState) => Promise<void>;
}

export function OnCallScheduleRow({
  row,
  selected,
  practitionerOptions,
  practitionersLoading,
  saveState,
  disabled = false,
  selectable = true,
  onSelectChange,
  onSave,
}: OnCallScheduleRowProps): ReactElement {
  const [localRow, setLocalRow] = useState(row);
  const isDirtyRef = useRef(false);
  const saveVersionRef = useRef(0);
  const inFlightSaveRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!isDirtyRef.current) {
      setLocalRow(row);
    }
  }, [row]);

  const backupOptions = useMemo(
    () => practitionerOptions.filter((option) => option.value !== localRow.primaryId),
    [practitionerOptions, localRow.primaryId]
  );

  const debouncedSave = useDebouncedCallback((nextRow: OnCallRowState) => {
    const version = ++saveVersionRef.current;

    void (async () => {
      if (inFlightSaveRef.current) {
        await inFlightSaveRef.current;
      }
      if (version !== saveVersionRef.current) {
        return;
      }

      const savePromise = onSave(nextRow);
      inFlightSaveRef.current = savePromise;

      try {
        await savePromise;
        if (version === saveVersionRef.current) {
          isDirtyRef.current = false;
        }
      } finally {
        if (inFlightSaveRef.current === savePromise) {
          inFlightSaveRef.current = null;
        }
      }
    })();
  }, 300);

  const updateRow = (partial: Partial<OnCallRowState>) => {
    const nextRow: OnCallRowState = { ...localRow, ...partial };
    isDirtyRef.current = true;
    setLocalRow(nextRow);

    const practitionerChanged = partial.primaryId !== undefined || partial.backupId !== undefined;
    const timesChanged = partial.startTime !== undefined || partial.endTime !== undefined;
    const hasPrimary = !!nextRow.primaryId;
    const hasValidTimes = !!nextRow.startTime && !!nextRow.endTime;

    if (practitionerChanged && hasPrimary && hasValidTimes) {
      debouncedSave(nextRow);
      return;
    }

    if (practitionerChanged && !hasPrimary) {
      debouncedSave(nextRow);
      return;
    }

    if (timesChanged && hasPrimary && hasValidTimes) {
      debouncedSave(nextRow);
    }
  };

  const endDateLabel = getOnCallEndDateLabel(localRow.date, localRow.startTime, localRow.endTime);

  return (
    <Table.Tr>
      {selectable && (
        <Table.Td>
          <Checkbox
            checked={selected}
            disabled={disabled}
            onChange={(event) => onSelectChange(event.currentTarget.checked)}
          />
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <span>{formatDate(localRow.date, 'ddd, MMMM Do')}</span>
          {saveState?.saving ? <Loader size="xs" /> : null}
          {saveState?.error ? (
            <Tooltip label={saveState.error}>
              <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
            </Tooltip>
          ) : null}
        </Group>
      </Table.Td>
      <Table.Td>
        <Select
          placeholder="Select a primary contact"
          data={practitionerOptions}
          value={localRow.primaryId}
          disabled={disabled || practitionersLoading}
          searchable
          clearable
          onChange={(value) =>
            updateRow({
              primaryId: value ?? null,
              backupId: value === localRow.backupId ? null : localRow.backupId,
            })
          }
        />
      </Table.Td>
      <Table.Td>
        <Select
          placeholder="Optional backup contact"
          data={backupOptions}
          value={localRow.backupId}
          disabled={disabled || practitionersLoading || !localRow.primaryId}
          searchable
          clearable
          onChange={(value) => updateRow({ backupId: value ?? null })}
        />
      </Table.Td>
      <Table.Td>
        <TimeInput
          value={localRow.startTime}
          disabled={disabled}
          onChange={(event) => updateRow({ startTime: event.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <TimeInput
            value={localRow.endTime}
            disabled={disabled}
            onChange={(event) => updateRow({ endTime: event.currentTarget.value })}
          />
          <Badge variant="light" color="gray">
            ({endDateLabel})
          </Badge>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
